import React, { useState, useEffect, useRef } from 'react';
import {
  CCard, CCardBody, CCardHeader,
  CForm, CFormLabel, CFormInput, CButton,
  CAlert, CSpinner, CBadge,
} from '@coreui/react';
import api from '../services/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function useGoogleScript() {
  const [ready, setReady] = useState(typeof window.google !== 'undefined');
  useEffect(() => {
    if (ready || !GOOGLE_CLIENT_ID) return;
    const existing = document.getElementById('google-gsi-script');
    if (existing) { setReady(true); return; }
    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setReady(true);
    document.head.appendChild(script);
  }, [ready]);
  return ready;
}

export default function MyProfilePage() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ nama: '', email: '', password: '', password2: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);  // { type: 'success'|'danger', text }
  const [enrolling, setEnrolling] = useState(false);
  const btnDivRef = useRef(null);
  const googleReady = useGoogleScript();

  useEffect(() => {
    api.get('/profile').then(r => {
      setProfile(r.data.profile);
      setForm(f => ({
        ...f,
        nama: r.data.profile.nama || '',
        email: r.data.profile.email || '',
      }));
    }).catch(() => setMsg({ type: 'danger', text: 'Failed to load profile.' }));
  }, []);

  // Render the Google One-Tap "Sign in with Google" button inside btnDivRef
  useEffect(() => {
    if (!googleReady || !GOOGLE_CLIENT_ID || !btnDivRef.current || profile?.google_email) return;
    if (typeof window.google === 'undefined') return;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleEnroll,
      auto_select: false,
    });

    window.google.accounts.id.renderButton(btnDivRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: 280,
    });
  }, [googleReady, profile]);

  async function handleGoogleEnroll(googleResponse) {
    setEnrolling(true);
    setMsg(null);
    try {
      const { data } = await api.post('/auth/google/enroll', { credential: googleResponse.credential });
      setProfile(p => ({ ...p, google_email: data.google_email }));
      setMsg({ type: 'success', text: `Google account connected: ${data.google_email}` });
    } catch (err) {
      setMsg({ type: 'danger', text: err.response?.data?.message || 'Failed to connect Google account.' });
    } finally {
      setEnrolling(false);
    }
  }

  async function handleUnenroll() {
    if (!window.confirm('Disconnect your Google account?')) return;
    setMsg(null);
    try {
      await api.delete('/auth/google/enroll');
      setProfile(p => ({ ...p, google_email: null }));
      setMsg({ type: 'success', text: 'Google account disconnected.' });
    } catch (err) {
      setMsg({ type: 'danger', text: err.response?.data?.message || 'Failed to disconnect.' });
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setMsg(null);
    if (form.password && form.password !== form.password2) {
      setMsg({ type: 'danger', text: 'Passwords do not match.' });
      return;
    }
    setSaving(true);
    try {
      const body = {};
      if (form.nama)     body.nama = form.nama;
      if (form.email)    body.email = form.email;
      if (form.password) body.password = form.password;
      await api.put('/profile', body);
      setMsg({ type: 'success', text: 'Profile saved.' });
      setForm(f => ({ ...f, password: '', password2: '' }));
    } catch (err) {
      setMsg({ type: 'danger', text: err.response?.data?.message || 'Failed to save profile.' });
    } finally {
      setSaving(false);
    }
  }

  if (!profile) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
        <CSpinner color="success" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <h4 className="mb-4">My Profile</h4>

      {msg && (
        <CAlert color={msg.type} dismissible onClose={() => setMsg(null)}>
          {msg.text}
        </CAlert>
      )}

      {/* ── Profile form ── */}
      <CCard className="mb-4">
        <CCardHeader><strong>Account Details</strong></CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleSave}>
            <div className="mb-3">
              <CFormLabel>Username</CFormLabel>
              <CFormInput value={profile.username} disabled />
            </div>
            <div className="mb-3">
              <CFormLabel>Full Name</CFormLabel>
              <CFormInput
                value={form.nama}
                onChange={e => setForm(f => ({ ...f, nama: e.target.value }))}
                placeholder="Your name"
              />
            </div>
            <div className="mb-3">
              <CFormLabel>Email</CFormLabel>
              <CFormInput
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="your@email.com"
              />
            </div>
            <div className="mb-3">
              <CFormLabel>New Password <span style={{ fontWeight: 400, color: '#888', fontSize: 12 }}>(leave blank to keep current)</span></CFormLabel>
              <CFormInput
                type="password"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="New password"
                autoComplete="new-password"
              />
            </div>
            {form.password && (
              <div className="mb-3">
                <CFormLabel>Confirm Password</CFormLabel>
                <CFormInput
                  type="password"
                  value={form.password2}
                  onChange={e => setForm(f => ({ ...f, password2: e.target.value }))}
                  placeholder="Repeat new password"
                  autoComplete="new-password"
                />
              </div>
            )}
            <CButton type="submit" color="success" disabled={saving}>
              {saving ? <CSpinner size="sm" className="me-2" /> : null}
              Save Changes
            </CButton>
          </CForm>
        </CCardBody>
      </CCard>

      {/* ── Google OAuth ── */}
      <CCard>
        <CCardHeader><strong>Google Sign-In</strong></CCardHeader>
        <CCardBody>
          {profile.google_email ? (
            <div>
              <p className="mb-2" style={{ fontSize: 14 }}>
                Connected as:{' '}
                <CBadge color="success" style={{ fontSize: 13 }}>
                  {profile.google_email}
                </CBadge>
              </p>
              <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                You can sign in using Google on the login page.
              </p>
              <CButton color="danger" variant="outline" size="sm" onClick={handleUnenroll}>
                Disconnect Google Account
              </CButton>
            </div>
          ) : (
            <div>
              <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                Connect your Google account to enable one-tap sign-in on mobile and desktop.
              </p>
              {!GOOGLE_CLIENT_ID ? (
                <CAlert color="warning" className="mb-0" style={{ fontSize: 13 }}>
                  Google Sign-In is not configured. Set <code>REACT_APP_GOOGLE_CLIENT_ID</code> to enable this feature.
                </CAlert>
              ) : (
                <div>
                  {enrolling && <CSpinner size="sm" className="me-2" />}
                  <div ref={btnDivRef} />
                </div>
              )}
            </div>
          )}
        </CCardBody>
      </CCard>
    </div>
  );
}
